// Chakra Imports
import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Icon,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";
import { SidebarContext } from "contexts/SidebarContext";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { CgMenuRight } from "react-icons/cg";
import { useLocation } from "react-router-dom";
import AdminNavbarLinks from "./AdminNavbarLinks";

export default function AdminNavbar(props) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  // Home opens on the near-black hero and the navbar floats directly over it; every other page
  // starts on the light page background. A translucent surface over the hero turns mid-grey, so
  // no grey text can reach AA against it — the text has to flip to white instead.
  const overDarkHero =
    location.pathname === "/" || location.pathname.startsWith("/home");
  const {
    sidebarWidth,
    setSidebarWidth,
    toggleSidebar,
    setToggleSidebar,
  } = useContext(SidebarContext);
  const {
    variant,
    children,
    fixed,
    secondary,
    brandText,
    onOpen,
    ...rest
  } = props;

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let mainText = useColorModeValue("gray.700", "gray.200");
  let secondaryText = useColorModeValue("gray.400", "gray.200");
  let navbarPosition = "absolute";
  let navbarFilter = "none";
  let navbarBackdrop = "blur(30px) saturate(140%)";
  let secondaryMargin = "0px";
  let paddingS = "15px";
  let paddingX = "15px";

  // The navbar floats over page content, and the home hero behind it is near-black. It used to
  // be fully transparent until scrolled, which left the breadcrumb and the language switcher
  // unreadable at the top of that page. It now always carries its own frosted surface so its
  // contents stay legible over any background.
  // Kept deliberately light. The heavy blur below is what carries legibility at this opacity —
  // over the near-black hero a 55% white surface still lands around 5:1 against gray.700 text,
  // which clears WCAG AA. Going much lower starts to fail it.
  const restingBg = useColorModeValue(
    "linear-gradient(112.83deg, rgba(255, 255, 255, 0.58) 0%, rgba(255, 255, 255, 0.52) 110.84%)",
    "linear-gradient(112.83deg, rgba(26, 35, 52, 0.58) 0%, rgba(19, 27, 41, 0.52) 110.84%)"
  );
  const restingBorder = useColorModeValue(
    "rgba(255, 255, 255, 0.55)",
    "rgba(255, 255, 255, 0.10)"
  );
  const restingShadow = useColorModeValue(
    "0px 2px 10px rgba(16, 26, 46, 0.06)",
    "0px 2px 10px rgba(0, 0, 0, 0.22)"
  );
  const scrolledShadow = useColorModeValue(
    "0px 7px 23px rgba(16, 26, 46, 0.16)",
    "0px 7px 23px rgba(0, 0, 0, 0.42)"
  );
  const scrolledFilter = useColorModeValue(
    "none",
    "drop-shadow(0px 7px 23px rgba(0, 0, 0, 0.05))"
  );

  let navbarShadow = restingShadow;
  let navbarBg = restingBg;
  let navbarBorder = restingBorder;

  if (overDarkHero) {
    // Barely-there tint over the hero — the blur does the work, and white text sits at ~18:1.
    navbarBg =
      "linear-gradient(112.83deg, rgba(10, 16, 32, 0.30) 0%, rgba(10, 16, 32, 0.16) 110.84%)";
    navbarBorder = "rgba(255, 255, 255, 0.16)";
    navbarShadow = "none";
    mainText = "white";
    secondaryText = "rgba(255, 255, 255, 0.72)";
  }

  if (props.fixed === true)
    if (scrolled === true) {
      navbarPosition = "fixed";
      navbarShadow = scrolledShadow;
      navbarFilter = scrolledFilter;
    }
  if (props.secondary) {
    navbarBackdrop = "none";
    navbarPosition = "absolute";
    mainText = "white";
    secondaryText = "white";
    secondaryMargin = "22px";
    paddingS = "40px";
    paddingX = "30px";
  }
  // Previously registered on every render with no cleanup, which piled up scroll listeners.
  useEffect(() => {
    const changeNavbar = () => setScrolled(window.scrollY > 1);
    changeNavbar();
    window.addEventListener("scroll", changeNavbar, { passive: true });
    return () => window.removeEventListener("scroll", changeNavbar);
  }, []);

  return (
    <Flex
      position={navbarPosition}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      borderWidth="1.5px"
      borderStyle="solid"
      transitionDelay="0s, 0s, 0s, 0s"
      transitionDuration=" 0.25s, 0.25s, 0.25s, 0s"
      transition-property="box-shadow, background-color, filter, border"
      transitionTimingFunction="linear, linear, linear, linear"
      alignItems={{ xl: "center" }}
      borderRadius="16px"
      display="flex"
      minH="75px"
      justifyContent={{ xl: "center" }}
      lineHeight="25.6px"
      mt={secondaryMargin}
      pb="8px"
      px={{
        base: paddingX,
        md: "30px",
      }}
      ps={{
        base: paddingS,
        md: "20px",
      }}
      pt="8px"
      top="18px"
      /* Pinned by both edges rather than by width. It used to combine right:30px with
         width:calc(100vw - 30px), which put the left edge at 0 and left a 30px gap only on the
         right — visibly lopsided on phones. Both-edge insets also avoid 100vw, which counts the
         scrollbar and can push the page into horizontal overflow. */
      left={{ base: "15px", xl: `${sidebarWidth + 30}px` }}
      right={{ base: "15px", xl: "30px" }}
      w="auto"
    >
      {/* Single row at every width. Stacking the links under the breadcrumb on small screens
          made the navbar three rows tall, and since it is absolutely positioned it covered the
          hero headline behind it instead of pushing it down. */}
      <Flex w="100%" flexDirection="row" alignItems="center" gap="12px">
        <Box minW="0">
          {/* Breadcrumb is the template's "Pages / <page>" trail with dead links; it is the
              first thing to drop when horizontal room is tight. */}
          <Breadcrumb display={{ base: "none", md: "block" }}>
            <BreadcrumbItem color={mainText}>
              <BreadcrumbLink href="#" color={secondaryText}>
                Pages
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbItem color={mainText}>
              <BreadcrumbLink href="#" color={mainText}>
                {brandText}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {/* Here we create navbar brand, based on route name */}
          <Link
            color={mainText}
            href="#"
            bg="inherit"
            borderRadius="inherit"
            fontWeight="bold"
            noOfLines={1}
            display="block"
            _hover={{ color: { mainText } }}
            _active={{
              bg: "inherit",
              transform: "none",
              borderColor: "transparent",
            }}
            _focus={{
              boxShadow: "none",
            }}
          >
            {brandText}
          </Link>
        </Box>
        <Box ms="auto" flex="none">
          <AdminNavbarLinks
            onOpen={props.onOpen}
            logoText={props.logoText}
            secondary={props.secondary || overDarkHero}
            fixed={props.fixed}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
  variant: PropTypes.string,
  secondary: PropTypes.bool,
  fixed: PropTypes.bool,
  onOpen: PropTypes.func,
};
